<template>
    <div class="wrapper d-flex h-100">
        <UpgradeNeeded
            v-if="upgrade.enabled && upgrade.type === 'modal'"
            :plan="upgrade.plan"
            @close="upgradeModalClose"
            @upgraded="$feature.continue()"
        />
        <div id="contents" :class="{ 'mobile-contents': isMobile }">
            <NotificationPermissionAlert
                v-if="isNotifyShowing && !isAdmin && !notifyIgnored && false"
                @close="isNotifyShowing = false"
            />
            <TrialFinishedModal />
            <Header />
            <div class="main-content">
                <Sidebar />
                <div class="content-view" :class="{ 'menu-active': menu.isOpen || menu.hoverActive }">
                    <SuspendedWarning v-if="isSuspendShowed()" />
                    <CreditCardRequiredWarning v-else-if="showCreditCardAlert" />
                    <template v-else>
                        <UpgradeNeededPageOverlay
                            v-if="upgrade.enabled && upgrade.type === 'overlay'"
                            :plan="upgrade.plan"
                            :feature="upgrade.feature"
                            :name="upgrade.name"
                        />
                        <template v-else>
                            <UpgradeNeededPageOverlay
                                v-if="upgrade.enabled && upgrade.type === 'overlay-limited'"
                                :plan="upgrade.plan"
                                :feature="upgrade.feature"
                                :name="upgrade.name"
                                :limited="true"
                            />
                            <router-view class="content-view-child" />
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Sidebar from "../components/Sidebar.vue";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { mapActions, mapState, mapGetters } from "vuex";
import NotificationPermissionAlert from "./NotificationPermissionAlert.vue";
import UpgradeNeededPageOverlay from "../components/billing/UpgradeNeededPageOverlay.vue";
import UpgradeNeeded from "../components/billing/UpgradeNeeded.vue";
import CreditCardRequiredWarning from "../components/billing/CreditCardRequiredWarning.vue";
import TrialFinishedModal from "../components/billing/TrialFinishedModal.vue";
import SuspendedWarning from "../components/SuspendedWarning.vue";
import router from "../router";

export default {
    name: "Layout",
    components: {
        UpgradeNeeded,
        UpgradeNeededPageOverlay,
        Sidebar,
        Header,
        Footer,
        NotificationPermissionAlert,
        CreditCardRequiredWarning,
        TrialFinishedModal,
        SuspendedWarning,
    },
    data() {
        return {
            isNotifyShowing: Notification.permission !== "granted",
        };
    },
    computed: {
        ...mapGetters({
            isTrialMode: "site/isTrialMode",
            isCreditCardCustomer: "site/isCreditCardCustomer",
            paidSubscriptions: "site/paidSubscriptions",
            isSavedCreditCard: "site/isSavedCreditCard",
            checkPermission: "auth/checkPermission",
        }),
        ...mapState({
            currentPlanInitialized: (state) => state.site.currentPlanInitialized,
            plansAvailable: (state) => state.site.plansAvailable,
            menu: (state) => state.layout.menu,
            user: (state) => state.auth.user,
            token: (state) => state.auth.token,
            adminUser: (state) => state.auth.adminUser,
            isAdmin: (state) => !!state.auth.adminUser,
            offlineMode: (state) => state.setting.offlineMode,
            largeHeader: (state) => state.app.largeHeader,
            isMobile: (state) => state.app.isMobile,
            upgrade: (state) => state.app.upgrade,
            isRoot: (state) => state.auth.user.role.isRoot,
            trialFinishedAlertShowed: (state) => state.setting.settings.trialFinishedAlertShowed,
            settingsInitialized: (state) => state.setting.initialized,
            site: (state) => state.site.site,
            route: (state) => state.app.route,
        }),
        notifyIgnored() {
            return sessionStorage.getItem("dismissed-notify-alert") === "true";
        },
        showCreditCardAlert() {
            return (
                this.settingsInitialized &&
                !this.isAdmin &&
                !this.isTrialMode &&
                this.currentPlanInitialized &&
                this.plansAvailable &&
                this.isCreditCardCustomer &&
                this.paidSubscriptions.length > 0 &&
                !this.isSavedCreditCard
            );
        },
    },
    methods: {
        ...mapActions({
            getUser: "auth/profile",
            getAdminUser: "auth/adminProfile",
            setUpgrade: "app/setUpgrade",
        }),
        isSuspendShowed() {
            const suspendReason = this.site.suspendReason;
            const blocks = {
                "payment": !this.$route.meta.suspendAllow,
                "trial-expired": !this.$route.meta.suspendAllow,
                "admin-suspend": true,
                "other": true,
            }
            console.log("isSuspendShowed", this.site.suspend, blocks[suspendReason]);
            return this.site.suspend && (this.isAdmin ? false : blocks[suspendReason]);
        },
        upgradeModalClose() {
            this.setUpgrade({
                enabled: false,
            });
        },
    },
    mounted() {
        if (this.user) {
            this.getUser();
        }

        if (this.adminUser) {
            this.getAdminUser();
        }
    },
};
</script>

<style lang="scss">
$breadcrumb-height: 45px;

#contents.mobile-contents {
    .page-content-wrapper {
        margin-left: 60px;
    }
}

.wrapper {
    .a-link {
        color: var(--a-tag-color) !important;
        text-decoration: underline;
    }

    .main-content {
        display: flex;
        width: 100vw;
        overflow: hidden;
        height: calc(100vh - 55px);
        position: relative;
        background-color: var(--color-bg-neutral-default);

        .content-view {
            border-top-left-radius: 20px;
            height: 100%;
            border-top: 1px solid var(--color-border-default);
            border-left: 1px solid var(--color-border-default);
            width: 100%;
            background: var(--color-bg-default) !important;
            overflow-x: hidden;
            overflow-y: auto;
            position: relative;

            .content-view-child {
                background-color: var(--color-bg-default) !important;
                height: 100%;

                .route-page-content {
                    height: 100%;

                    &.breadcrumb-content {
                        height: calc(100% - $breadcrumb-height);
                    }

                    width: 100%;
                    overflow-y: auto;
                    overflow-x: hidden;
                    display: flex;
                    align-items: flex-start;
                }

                .page-content-wrapper {
                    height: 100%;
                    width: 100%;
                    overflow: auto;
                    overflow-x: hidden;
                }
            }
        }
    }
}
</style>
